import { LoginRequest, endpoints, type UserPublic } from '@gopinhos/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { apiClient, bindAccessTokenStore } from './api-client';

interface AuthState {
  user: UserPublic | null;
  status: 'loading' | 'anonymous' | 'authenticated';
  login: (input: { email: string; password: string; totp?: string }) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface LoginResp {
  user: UserPublic & { mfaEnabled: boolean };
  accessToken: string;
  expiresIn: number;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const tokenRef = useRef<string | null>(null);
  const expiryRef = useRef<number | null>(null);
  const [user, setUser] = useState<UserPublic | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  // Wire token store for the api-client.
  useEffect(() => {
    bindAccessTokenStore({
      get: () => tokenRef.current,
      set: (token, expires) => {
        tokenRef.current = token;
        expiryRef.current = token && expires ? Date.now() + expires * 1000 : null;
      },
    });
  }, []);

  // Bootstrap: try to refresh on first mount; if refresh succeeds we have a session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(endpoints.refresh(), {
          method: 'POST',
          credentials: 'include',
        });
        if (!cancelled && res.ok) {
          const json = (await res.json()) as { accessToken: string; expiresIn: number };
          tokenRef.current = json.accessToken;
          expiryRef.current = Date.now() + json.expiresIn * 1000;
          // Fetch /users/:id (self) — we need the role for routing.
          // Decoded from the JWT payload to avoid the extra round trip.
          const payload = decodeJwtPayload(json.accessToken);
          if (payload) {
            setUser({
              id: payload.sub,
              email: payload.email,
              fullName: '',
              avatarUrl: null,
              role: payload.role,
              status: 'active',
            });
          }
          setStatus('authenticated');
        } else if (!cancelled) {
          setStatus('anonymous');
        }
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (input: { email: string; password: string; totp?: string }) => {
    const validated = LoginRequest.parse({
      email: input.email,
      password: input.password,
      ...(input.totp ? { totp: input.totp } : {}),
    });
    const json = await apiClient.request<LoginRequest, never>({
      method: 'POST',
      path: endpoints.login(),
      body: validated,
    }) as unknown as LoginResp;
    tokenRef.current = json.accessToken;
    expiryRef.current = Date.now() + json.expiresIn * 1000;
    setUser({
      id: json.user.id,
      email: json.user.email,
      fullName: json.user.fullName,
      avatarUrl: json.user.avatarUrl,
      role: json.user.role,
      status: json.user.status,
    });
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.request({ method: 'POST', path: endpoints.logout() });
    } catch {
      /* ignore — proceed to clear local state */
    }
    tokenRef.current = null;
    expiryRef.current = null;
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, status, login, logout }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth used outside AuthProvider');
  return ctx;
}

interface DecodedPayload {
  sub: string;
  email: string;
  role: UserPublic['role'];
  exp: number;
}

function decodeJwtPayload(token: string): DecodedPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    return JSON.parse(json) as DecodedPayload;
  } catch {
    return null;
  }
}
